import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom';
import "./Home.css"
import Navbar from '../../Common/Navbar/Navbar';
import Footer from '../../Common/Footer/Footer';
import { useSelector } from 'react-redux';
import { send } from "@emailjs/browser";
import GradientBtn from '../../Common/GradientBtn/GradientBtn';
import { useMediaQuery } from "@mui/material";

/**
 * Home Component
 * 
 * The main landing page of the application, featuring:
 * - Hero section with dynamic video background (Light/Dark mode support)
 * - Background/About section
 * - Features grid
 * - Solutions showcase (TrafficEye, TransitEye, CrowdEye)
 * - Case Studies
 * - Testimonials/Partner feedback
 * - Contact form and CTA
 * 
 * Handles theme-aware rendering for specific assets like the Hero video.
 */
function Home() {
  const smallScreen = useMediaQuery("(max-width:600px)");
  const functionSlice = useSelector(state => state.functionslice.value);
  const location = useLocation();
  const [isBackgroundExpanded, setIsBackgroundExpanded] = useState(false);

  const LightModeVideo = "https://pm-website-assets.s3.ap-south-1.amazonaws.com/Assets/Futuristic_City_Animation_Generation.mp4";

  const HeroVideo = "https://pm-website-assets.s3.ap-south-1.amazonaws.com/Assets/Futuristic_Indian_City_Traffic_Animation.mp4";

  const [formInput, setFormInput] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormInput({ ...formInput, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = formInput.name.trim();
    const email = formInput.email.trim();
    const organization = formInput.organization.trim();
    const message = formInput.message.trim();

    if (name === "" || email === "" || message === "") {
      alert("Please fill in all required fields.");
      return;
    }

    send("service_led1pu5", "template_fo0s97s", {
      from_name: name,
      email: email,
      organization: organization,
      message: message,
    }).then(
      (res) => {
        alert("Email Sent");
        setFormInput({ name: "", email: "", organization: "", message: "" });
      },
      (err) => {
        alert("Something Went Wrong. Please try again later.");
      }
    );
  };

  useEffect(() => {
    // Reset scroll to top on load unless hash exists
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <>
      <Navbar />

      {/* Shared Gradient Definition */}
      {/* Shared Gradient Definition - MOVED TO NAVBAR */}
      {/* Remaining definitions if any specific to Home */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="gradVertical" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#D8FF91" />
            <stop offset="100%" stopColor="#81CB50" />
          </linearGradient>
        </defs>
      </svg>

      {/* HERO SECTION */}
      <section className="home-hero-container">
        <div className="home-hero-content">
          <h1>Revolutionizing Mobility With <span className="highlight-text">Intelligent Solutions</span></h1>
          <p>Intelligent mobility engineered for tomorrow’s cities.</p>
          <div className="home-hero-btns">
            <Link to="/#solutions">
              <GradientBtn className="btn-primary">Explore</GradientBtn>
            </Link>
            <Link to="/contact">
              <GradientBtn className="btn-secondary contact-us-btn">Contact Us</GradientBtn>
            </Link>
          </div>
        </div>
        <div className="home-hero-media">
          <div className="hero-video-wrapper">
            <video
              key={functionSlice.isDark ? 'dark' : 'light'}
              autoPlay
              loop
              muted
              playsInline
              className="hero-video"
            >
              <source src={functionSlice.isDark ? HeroVideo : LightModeVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div
          className="scroll-indicator"
          onClick={() => {
            const element = document.getElementById('background');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <p>scroll</p>
        </div>

        {/* AI Powered Badge - REMOVED */}
        {/* <div className="ai-badge">
          <h2>AI</h2>
          <p>Powered</p>
        </div> */}
      </section>

      {/* BACKGROUND SECTION */}
      <section className="home-background-section" id="background">
        <div className="home-background-content">
          {smallScreen ? <><div className="home-background-text background-card">
            <h2>Background</h2>

            <div className="background-text-preview">
              <p>
                {isBackgroundExpanded
                  ? "India's rapid urbanization has created unprecedented challenges in traffic management and urban mobility. With over 377 million vehicles on Indian roads and cities expanding at an exponential rate, traditional traffic management systems are struggling to cope with the complexity and scale of modern urban transportation needs."
                  : <>India's rapid urbanization has created<br />
                    unprecedented challenges in traffic management<br />
                    and urban mobility. With over 377 million vehicles<br />
                    on Indian roads and cities expanding at an</>
                }
              </p>
            </div>

            <p className="read-more" onClick={() => setIsBackgroundExpanded(!isBackgroundExpanded)}>
              {isBackgroundExpanded ?
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#8CC63F" />
                  <path d="M8 14L12 10L16 14" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                : "Read more ⌄"}
            </p>
          </div></> : <> <div className="home-background-text background-card">
            <h2>Background</h2>
            <p>
              Parimitra Pvt. Ltd. is a scientific spin-off from <span className="highlight-text">IIT Roorkee</span>, incorporated in 2024 with a
              mission to transform research into reality. Funded and incubated at Drishti CPS, IIT Indore,
              the innovation ecosystem has nurtured Parimitra's cutting-edge solutions from concept to
              reality. Born from the vision of bringing academic innovations to the market, we are building
              the smart cities of tomorrow.
            </p>
            <p>
              At Parimitra (Pari for Parivahan, Mitra for Friend), we design and develop AI-powered,
              edge-computing solutions that are indigenous, affordable, and scalable. Our technologies
              empower cities to become smarter, safer, and more sustainable from intelligent
              transportation to urban infrastructure optimization.
            </p>
            <p>
              With deep tech expertise and a self-reliant India-first approach, Parimitra is not just a
              company, but a trusted partner in shaping <span className="highlight-text">connected, future-ready cities.</span>
            </p>
          </div></>}

          <div className="home-background-image">
            <img src="/Images/CoverImages/backgroundsection-cover.png" alt="Parimitra Background" />
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section className="home-features-section">
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <img src="/Images/Icons/indiaflag.png" alt="India Flag" />
            </div>
            <h3>Beyond Border</h3>
            <p> {smallScreen
              ? "Our solutions transcend geographical boundaries, bringing world-class traffic management technology adapted for Indian conditions. We understand local challenges and deliver global-standard solutions."
              : "Proudly designed and developed in India, Parimitra builds future-ready urban intelligence solutions — innovated in India, for the world."}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <img src="/Images/Icons/buildings.png" alt="Buildings" />
            </div>
            <h3>Future Ready</h3>
            <p>{smallScreen
              ? "Built on scalable AI architecture, our systems evolve with your city. From autonomous vehicle integration to predictive analytics, we're ready for tomorrow's mobility challenges today."
              : "Aligned with Digital India, Parimitra builds intelligent, AI-driven urban solutions that make cities more connected, inclusive, and future-ready."}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <img src="/Images/Icons/school-bus.png" alt="School Bus" />
            </div>
            <h3>Research into Reality</h3>
            <p>{smallScreen ?
              "Emerging from the prestigious halls of IIT Roorkee, we turn deep academic insight into impactful real-world solutions."
              : "Born out of IIT Roorkee, we bridge the gap between academic research and real-world implementation to solve complex urban challenges."}</p>
          </div>
        </div>
      </section>

      {/* SOLUTIONS SECTION */}
      <section className="home-solutions-container" id="solutions">
        <h1>Our Smart Mobility Solutions</h1>
        <p>Parimitra's suite of AI-powered products transforms urban transportation infrastructure into intelligent, responsive systems.</p>

        <div className="home-solutions-grid">
          {/* TrafficEye Card */}
          <div className="solution-card">
            <div className="solution-card-image" style={{ backgroundImage: `url("/Images/CoverImages/trafficeye-cover.jpg")` }}></div>
            <div className="solution-card-content">
              <h3>Trafficeye</h3>
              <p>TrafficEye is a standalone AI-powered camera that delivers real-time traffic analytics. It enables smart parking, violation detection, and intelligent traffic enforcement.</p>
              <Link to="/products/trafficeye" className="learn-more">Learn more &rarr;</Link>
            </div>
          </div>

          {/* TransitEye Card */}
          <div className="solution-card">
            <div className="solution-card-image" style={{ backgroundImage: `url("/Images/CoverImages/transiteye-cover.png")` }}></div>
            <div className="solution-card-content">
              <h3>Transiteye</h3>
              <p>TransitEye is an AI-powered urban mobility system offering real-time public transport tracking and crowd insights to support smarter planning and operations.</p>
              <Link to="/products/transiteye" className="learn-more">Learn more &rarr;</Link>
            </div>
          </div>

          {/* CrowdEye Card */}
          <div className="solution-card">
            <div className="solution-card-image" style={{ backgroundImage: `url("/Images/CoverImages/crowdeye-cover.png")` }}></div>
            <div className="solution-card-content">
              <h3>Crowdeye</h3>
              <p>CrowdEye is a standalone AI camera for real-time crowd behavior analysis, enabling movement tracking and safety monitoring insights.</p>
              <Link to="/products/crowdeye" className="learn-more">Learn more &rarr;</Link>
            </div>
          </div>
        </div>

        <div className="home-solutions-cta">
          <Link to="/contact">
            <GradientBtn>Customise a solution</GradientBtn>
          </Link>
        </div>
      </section>

      {/* CASE STUDY SECTION */}
      <section className="home-case-study-section">
        <div className="case-study-card">
          {smallScreen ?
            <div className="case-study-content">
              <h2>Case Study: Trafficeye at Meerut</h2>
              <p className="case-description">TrafficEye was deployed across 10 major intersections in Meerut to extract real-time classified turning movement counts under live traffic conditions.</p>

              <ul className="case-stats">
                <li>
                  <span className="check-icon"></span> Lower error rate: RMSE of 4.03 from edge devices
                </li>
                <li>
                  <span className="check-icon"></span> Higher accuracy: showed an accuracy of around mAP@0.5: 91.2%
                </li>
                <li>
                  <span className="check-icon"></span> On-device processing: No centralized servers or manual labeling required
                </li>
              </ul>

              <Link to="/case-studies/meerut" className="read-case-study">
                Read the full case study &nbsp; &gt;
              </Link>
            </div>
            :
            <div className="case-study-content">
              <h2>Case Study: Trafficeye at Meerut</h2>
              <p className="case-description">TrafficEye was deployed across 10 major intersections in Meerut to extract real-time classified turning movement counts under live traffic conditions.</p>

              <ul className="case-stats">
                <li>
                  <span className="check-icon">✓</span> Lower error rate: RMSE of 4.03 from edge devices
                </li>
                <li>
                  <span className="check-icon">✓</span> Higher accuracy: showed an accuracy of around mAP@0.5: 91.2%
                </li>
                <li>
                  <span className="check-icon">✓</span> On-device processing: No centralized servers or manual labeling required
                </li>
              </ul>

              <Link to="/case-studies/meerut" className="read-case-study">
                Read the full case study &rarr;
              </Link>
            </div>}
          <div className="case-study-image" style={{ backgroundImage: `url("/Images/CoverImages/meerutai.png")` }}>
          </div>
        </div>
      </section>

      {/* PARTNERS / TESTIMONIALS SECTION - HIDDEN */}
      {/* <section className="home-testimonials-container">
        <h1>What Experts Say</h1>
        <p>Expert insights shaped by real-world implementation.</p>

        <div className="home-testimonials-grid">
      
          <div className="testimonial-card" style={{ position: 'relative' }}>
            <div className="testimonial-cardcover"></div>
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="#8CC63F">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
              ))}
            </div>
            <p className="testimonial-text">"Parimitra's TrafficPulse AI has transformed how we manage traffic flow in our downtown core. Implementation was smooth, and the results were immediate and impressive."</p>
            <div className="testimonial-author">
              <img src={RajivImg} alt="Rajiv Mehta" />
              <div>
                <h4>Rajiv Mehta</h4>
                <p>Urban Planning Director, Bangalore</p>
              </div>
            </div>

          </div>

   
          <div className="testimonial-card" style={{ position: 'relative' }}>
            <div className="testimonial-cardcover"></div>
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="#8CC63F">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
              ))}
            </div>
            <p className="testimonial-text">"Parimitra's TrafficPulse AI has transformed how we manage traffic flow in our downtown core. Implementation was smooth, and the results were immediate and impressive."</p>
            <div className="testimonial-author">
              <img src={RajivImg} alt="Rajiv Mehta" />
              <div>
                <h4>Rajiv Mehta</h4>
                <p>Urban Planning Director, Bangalore</p>
              </div>
            </div>

          </div>

       
          <div className="testimonial-card" style={{ position: 'relative' }}>
            <div className="testimonial-cardcover"></div>
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="#8CC63F">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
              ))}
            </div>
            <p className="testimonial-text">"Parimitra's TrafficPulse AI has transformed how we manage traffic flow in our downtown core. Implementation was smooth, and the results were immediate and impressive."</p>
            <div className="testimonial-author">
              <img src={RajivImg} alt="Rajiv Mehta" />
              <div>
                <h4>Rajiv Mehta</h4>
                <p>Urban Planning Director, Bangalore</p>
              </div>
            </div>

          </div>
        </div>
      </section> */}

      {/* CALL TO ACTION BANNER */}
      <section className="home-cta-banner">
        <h1>Ready to transform your city's mobility?</h1>
        <p>Get customizable solutions tailored to your specific needs—whether it is individual-level management, large-scale data collection, or end-to-end smart city transformation. <span>Parimitra</span> delivers purpose-built, data-driven solutions curated for your use case.</p>
        <Link to="/contact">
          <GradientBtn>Schedule a Consultation</GradientBtn>
        </Link>
      </section>

      {/* CONTACT FORM SECTION */}
      {/* <section className="home-contact-section">
        <div className="home-contact-info">
          <h1>Get in Touch</h1>
          <p>Looking to deploy intelligent, scalable systems for your city or organization? Share your requirements with us, and our team will connect with you to design the right solution for your needs.</p>

          <div className="contact-detail-item">
            <div className="icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#8CC63F">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
            <div>
              <h4>Headquarters</h4>
              <p>{smallScreen ? "Headquarters: " : ''}Roorkee, Uttarakhand<br /></p>
            </div>
          </div>
          <div className="contact-detail-item">
            <div className="icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#8CC63F">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </div>
            <div>
              <h4>Email</h4>
              <p>support@parimitra.in</p>
            </div>
          </div>
          <div className="contact-detail-item">
            <div className="icon">
              <img src="/Images/Icons/phone.png" alt="Phone" style={{ width: '24px', height: '24px' }} />
            </div>
            <div>
              <h4>Phone</h4>
              <p>+91 9495050608</p>
            </div>
          </div> */}

          
          {/* Facebook,insta and twitter buttons commented out as they dont exist for now */}
          {/* <div className="social-icons">
            <a href="#" className="social-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#8CC63F">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
              </svg>
            </a>
            <a href="#" className="social-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#8CC63F">
                <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05a4.28 4.28 0 0 0-4.09 3c-3.54-.18-6.68-1.88-8.78-4.46a4.28 4.28 0 0 0 1.32 5.71 4.29 4.29 0 0 1-1.94-.53v.05c0 2.07 1.47 3.8 3.42 4.19-.36.1-.74.15-1.13.15-.27 0-.54-.03-.8-.08.54 1.69 2.11 2.92 3.97 2.96-1.46 1.15-3.3 1.83-5.29 1.83-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
              </svg>
            </a>
            <a href="#" className="social-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#8CC63F">
                <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
              </svg>
            </a>
          </div> 
        </div>*/}

        

          {/* The CONTACT SECTION is changed to a redirect to the CONTACT.  */}

          
        {/* <div className="home-contact-form-wrapper">  
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  name="name"
                  value={formInput.name}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Your email"
                  name="email"
                  value={formInput.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Organization</label>
              <input
                type="text"
                placeholder="Your organization"
                name="organization"
                value={formInput.organization}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                placeholder="How can we help you?"
                rows="4"
                name="message"
                value={formInput.message}
                onChange={handleChange}
              ></textarea>
            </div>
            <div style={{ marginTop: '20px' }}>
              <GradientBtn type="submit" className="submit-btn" style={{ width: '100%' }}>Send Message</GradientBtn>
            </div>
          </form> 
          
          </div>

          
          {smallScreen ? (
                  <div className="redirect-card mobile-product-cta">
                    <h3>Bring Intelligent <br />Mobility to Your City</h3>
                    <p>Transform your urban infrastructure with AI-powered traffic monitoring that delivers real results and measurable impact.</p>
                    <Link to="/contact" className="btn-get-started">
                      Request Demo
                    </Link>
                  </div>
                ) : (<div className="redirect-card">
                    <h3>Ready to Transform Your City?</h3>
                    <p>Join the expanding ecosystem of smart cities adopting Parimitra's AI-powered traffic intelligence to transform urban mobility, streamline transportation networks, and create safer, more responsive infrastructure for the future</p>
                    <Link
                      to="/contact"
                      className="btn-get-started"
                      style={{ backgroundColor: "#fff", color: "var(--accent-color)" }}
                    >
              Schedule Consultation
            </Link>
          </div>
          )}
        
      </section> */}

      <Footer />
    </>
  )
}

export default Home